import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";

import { Headline } from "@fronterahealth/frontera-ui-components";

import {
  ProviderFileCategoryEnum,
  useCreateProviderFileMutation,
  useDeleteProviderFileMutation,
  useMarkProviderFileAsUploadedMutation,
  useUpdateProviderProfileMutation,
} from "@api/graphql/types-and-hooks";
import { FormContainer, TripleColumnHeader } from "@components/forms/FormLayout";
import FormSubmitButton from "@components/forms/FormSubmitButton/FormSubmitButton";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { AvatarUpload } from "@pages/FronteraClarity/components/AvatarUpload";
import { useAdminData } from "@providers/AdminDataProvider";

// Generate half-hour time slots: "12:00 AM" ... "11:30 PM"
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? "00" : "30";
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const label = `${displayHour}:${minute} ${period}`;
  return { id: `${String(hour).padStart(2, "0")}:${minute}`, primary: label };
});

const TIMEZONE_OPTIONS = [
  { id: "America/New_York", primary: "Eastern Time (ET)" },
  { id: "America/Chicago", primary: "Central Time (CT)" },
  { id: "America/Denver", primary: "Mountain Time (MT)" },
  { id: "America/Phoenix", primary: "Mountain Time - Arizona (no DST)" },
  { id: "America/Los_Angeles", primary: "Pacific Time (PT)" },
  { id: "America/Anchorage", primary: "Alaska Time (AKT)" },
  { id: "Pacific/Honolulu", primary: "Hawaii Time (HT)" },
];

const formatTimeValue = (value: string | null | undefined, fallback: string): string => {
  if (!value) return fallback;
  return value.slice(0, 5);
};

const formatTimezoneValue = (value: string | null | undefined): string => {
  const match = TIMEZONE_OPTIONS.find((opt) => opt.id === value);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserMatch = TIMEZONE_OPTIONS.find((opt) => opt.id === browserTz);
  return match ? match.id : browserMatch ? browserTz : "America/New_York";
};

interface ProfileFormFields {
  firstName: string;
  lastName: string;
  clinicalCredentials: string;
  bio: string;
  workingHoursStart: string;
  workingHoursEnd: string;
  timezone: string;
  phoneNumber: string;
}

export const ProfileSettings: React.FC = () => {
  const { loginProviderDetailsQuery } = useAdminData();
  const updateProviderProfile = useUpdateProviderProfileMutation({});

  const { data } = loginProviderDetailsQuery;
  const providerMetadata = data?.getLoginProvider?.providermetadata;
  const profileImage = data?.getLoginProvider?.profileImage;

  const { user, isLoading: isAuth0Loading } = useAuth0();

  const resolvedImageUrl =
    profileImage?.signedS3ProviderFile ?? (!isAuth0Loading && user?.picture ? user.picture : null);

  const createProviderFile = useCreateProviderFileMutation({});
  const markProviderFileAsUploaded = useMarkProviderFileAsUploadedMutation({});
  const deleteProviderFile = useDeleteProviderFileMutation({});

  const deleteImage = async () => {
    if (profileImage) {
      await deleteProviderFile.mutateAsync({ fileId: profileImage.id });
    }
  };

  const handleFileChange = async (file: File) => {
    try {
      await deleteImage();

      const createRes = await createProviderFile.mutateAsync({
        input: {
          fileCategory: ProviderFileCategoryEnum.ProfileImage,
          fileSize: file.size,
          mimeType: file.type,
          originalFileName: file.name,
        },
      });

      const providerFile = createRes.createProviderFile?.providerFile;
      if (!providerFile?.signedS3Url || !providerFile?.id) {
        notifyError("Failed to initiate photo upload.");
        return;
      }

      const s3Response = await fetch(providerFile.signedS3Url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!s3Response.ok) {
        notifyError("Failed to upload photo. Please try again.");
        return;
      }

      await markProviderFileAsUploaded.mutateAsync({ fileId: providerFile.id });
    } catch {
      notifyError("Something went wrong uploading your photo.");
    } finally {
      loginProviderDetailsQuery.refetch();
    }
  };

  const handleRemoveImage = async () => {
    try {
      await deleteImage();
      loginProviderDetailsQuery.refetch();
    } catch {
      notifyError("Failed to remove photo.");
    }
  };

  const {
    onSubmit,
    RegisteredFormInput,
    RegisteredFormTextArea,
    RegisteredFormSelected,
    RegisteredPhoneNumberInput,
    formState,
    reset,
    watch,
  } = useFormUtils<ProfileFormFields>({
    mutationFn: async (data) => {
      const response = await updateProviderProfile.mutateAsync({
        providerData: {
          firstName: data.firstName,
          lastName: data.lastName,
          bio: data.bio,
          clinicalCredentials: data.clinicalCredentials,
          workingHoursStart: data.workingHoursStart,
          workingHoursEnd: data.workingHoursEnd,
          timezone: data.timezone,
          phoneNumber: data.phoneNumber ?? "",
        },
      });

      if (!response.updateProviderProfile?.status) {
        notifyError(response.updateProviderProfile?.message || "Failed to update profile.");
        return;
      }

      notifySuccess("Profile updated successfully.");
      loginProviderDetailsQuery.refetch();
    },
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");

  useEffect(() => {
    if (!providerMetadata) return;
    reset({
      firstName: providerMetadata.firstName ?? "",
      lastName: providerMetadata.lastName ?? "",
      clinicalCredentials: providerMetadata.clinicalCredentials ?? "",
      bio: providerMetadata.bio ?? "",
      workingHoursStart: formatTimeValue(providerMetadata.workingHoursStart, "09:00"),
      workingHoursEnd: formatTimeValue(providerMetadata.workingHoursEnd, "17:00"),
      timezone: formatTimezoneValue(providerMetadata.timezone),
      phoneNumber: providerMetadata.phoneNumber ?? "",
    });
  }, [providerMetadata]);

  return (
    <div className="flex flex-col items-center mt-8 max-w-4xl mx-auto w-full">
      <FormContainer onSubmit={onSubmit} className="w-full">
        <div className="flex flex-col rounded-md border bg-white p-6 w-full">
          <Headline size="medium">Profile</Headline>
          <div className="flex flex-row justify-center">
            <AvatarUpload
              imageUrl={resolvedImageUrl}
              firstName={firstName}
              lastName={lastName}
              isLoading={loginProviderDetailsQuery.isLoading}
              canRemove={!!profileImage}
              onFileChange={handleFileChange}
              onRemove={handleRemoveImage}
            />
          </div>

          <div className="mt-8 flex flex-col gap-4">
            <TripleColumnHeader>
              <RegisteredFormInput
                formKey="firstName"
                label="First Name"
                placeholder="Jane"
                formState={formState}
                required
              />
              <RegisteredFormInput formKey="lastName" placeholder="Doe" label="Last Name" formState={formState} />
              <RegisteredFormInput
                formKey="clinicalCredentials"
                label="Clinical Credentials"
                placeholder="e.g. BCBA, LPC, PhD"
                formState={formState}
                required={false}
              />
            </TripleColumnHeader>

            <RegisteredFormTextArea
              formKey="bio"
              label="Bio"
              placeholder="Tell families a bit about yourself..."
              rows={4}
              formState={formState}
              required={false}
            />

            <TripleColumnHeader>
              <RegisteredPhoneNumberInput
                formKey="phoneNumber"
                formState={formState}
                defaultCountry="US"
                label="Phone Number"
                required={false}
              />
            </TripleColumnHeader>

            <Headline className="mb-4">Working Hours</Headline>
            <TripleColumnHeader>
              <RegisteredFormSelected
                formKey="workingHoursStart"
                title="Start Time"
                placeholderText="Select start time"
                items={TIME_OPTIONS}
                formState={formState}
                required={false}
              />
              <RegisteredFormSelected
                formKey="workingHoursEnd"
                title="End Time"
                placeholderText="Select end time"
                items={TIME_OPTIONS}
                formState={formState}
                required={false}
              />
              <RegisteredFormSelected
                formKey="timezone"
                title="Timezone"
                placeholderText="Select timezone"
                items={TIMEZONE_OPTIONS}
                formState={formState}
                required={false}
              />
            </TripleColumnHeader>
          </div>
        </div>

        <div className="flex w-full justify-end mt-4">
          <FormSubmitButton isLoading={updateProviderProfile.isPending} buttonText="Save" />
        </div>
      </FormContainer>
    </div>
  );
};
